import { useState } from 'react'
import { useQuery, useMutation } from 'react-apollo'
import { useHistory } from 'react-router-dom'
import addToCart from 'graphql/addToCart'
import getCartItemsFromCache from 'graphql/getCartItemsFromCache'
import { persistCart } from 'utils/cart'
import { useBundleCart } from './useBundleCart'
import { log, handleSessionError } from 'utils/error-tracking'
import trackCartEvent from '../track-cart-event'

/**
 * @typedef {Object} CartItem
 * @property {number} quantity
 * @property {string} productName
 * @property {string} productTermsAndConditions
 * @property {string} urlKey
 * @property {string} sku
 * @property {string} imgUrl
 * @property {Boolean} isGifting
 * @property {string} recipientEmail
 * @property {string} senderName
 * @property {string} note
 * @property {string} selectedDay
 * @property {Object} customAmount: CustomAmount
 * @property {Object} amountToPay: Amount
 * @property {Object} totalAmount: Amount
 * @property {Object[]} discounts: DiscountInput[]
 */

/**
 * useCart hook for React.FunctionComponent
 * @returns {UseCart}
 */
export const useCart = () => {
  const { data } = useQuery(getCartItemsFromCache)
  const [updateCart, { client }] = useMutation(addToCart)
  const [loading, setLoading] = useState(false)
  const history = useHistory()
  const bundleCart = useBundleCart()

  const existingCart = data.cartItems

  /**
   * Concats existing cart and new items and returns a promise which
   * writes data to apollo cart.
   * @param {CartItem|CartItem[]} payload
   */
  function handleAddToCart(payload) {
    // Set loading state while Promise resolves
    setLoading(true)

    // If payload is not an Array, convert to an array with one item
    let newItems = payload
    if (!Array.isArray(payload)) newItems = [payload]

    persistCart(client, updateCart, existingCart, newItems)
      .then(res => {
        setLoading(false)

        // Clear all items from bundle cart
        bundleCart.clear()

        // Track items added to cart
        trackCartEvent('setCart', res)

        // Redirect to cart page
        history.push('/cartsummary')
      })
      .catch(error => {
        setLoading(false)
        handleSessionError(error)
        log({
          file: 'useCart',
          err: { stack: 'Error adding item to Cart from useCart hook' },
        })
      })
  }

  /**
   * @typedef {Object} UseCart
   * @property {function} handleAddToCart Adds items cart
   * @property {Boolean} loading returns true while async function processes cart items
   */
  return { handleAddToCart, loading }
}
